/**
 * 로그인 · 가입 (D8: 이메일/암호. 소셜 로그인은 마지막 단계에서 이 화면에 버튼으로 더합니다).
 *
 * 시안 03 L1 · L1b: 별 40 → 워드마크 → 부제 「차 한 잔 내어 줄게, 쉬었다 가」(10장 10) → 폼 → 아래 고정 버튼.
 * 성공하면 첫 방문 중이면 멈춘 장면(/welcome, /counter), 마쳤으면 들어오려던 화면으로 갑니다.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WordmarkStack } from '../../components/Brand.jsx';
import { TextField } from '../../components/TextField.jsx';
import { Button } from '../../components/Button.jsx';
import { errorMessageOf } from '../../api/client.js';
import * as authApi from '../../api/auth.js';
import { useSession } from '../../stores/session.js';
import { landingPathOf } from '../../components/RouteGuards.jsx';
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen.js';

const PASSWORD_MIN_LENGTH = 8;

export function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const setSession = useSession((state) => state.setSession);

    // 시작 화면(L0)에서 「시작하기」면 가입, 「이미 단골이에요」면 로그인으로 엽니다
    const [mode, setMode] = useState(location.state?.mode === 'signup' ? 'signup' : 'login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    // 앱에서 키보드가 올라오면 「가입하기 · 로그인」이 키보드 위에 보이게 내려 줌(DEV-13 0)
    const actionsRef = useRef(null);
    const keyboardOpen = useKeyboardOpen();
    useEffect(() => {
        if (keyboardOpen) {
            actionsRef.current?.scrollIntoView?.({ block: 'end' });
        }
    }, [keyboardOpen]);

    const isSignup = mode === 'signup';
    const canSubmit = email.trim() !== '' && password.length >= (isSignup ? PASSWORD_MIN_LENGTH : 1) && !busy;

    async function handleSubmit(event) {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const request = isSignup ? authApi.signup : authApi.login;
            const { token, user } = await request({ email: email.trim(), password });
            setSession({ token, user });
            const returnTo = location.state?.from ?? '/';
            navigate(landingPathOf(user, returnTo), { replace: true });
        } catch (requestError) {
            setError({
                field: requestError?.details?.field ?? null,
                message: errorMessageOf(requestError, isSignup ? '가입하지 못했어요.' : '로그인하지 못했어요.'),
            });
        } finally {
            setBusy(false);
        }
    }

    function switchMode() {
        setMode(isSignup ? 'login' : 'signup');
        setError(null);
    }

    return (
        <main className="flex min-h-dvh flex-col px-gutter pt-16 pb-6">
            {/* 세로 묶음: 찻잔 마크 → 워드마크 → 부제 (시안 04 5.17) */}
            <WordmarkStack textSize={44} lineHeight={52} subtitle="차 한 잔 내어 줄게, 쉬었다 가" />

            <form className="mt-10 flex flex-1 flex-col gap-4" onSubmit={handleSubmit} noValidate>
                <TextField
                    label="이메일"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    error={error?.field === 'email' ? error.message : null}
                />
                <TextField
                    label="암호"
                    type="password"
                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    hint={isSignup ? `${PASSWORD_MIN_LENGTH}자 이상` : null}
                    error={error?.field === 'password' ? error.message : null}
                />
                {error && !error.field ? (
                    <p role="alert" className="m-0 text-caption text-error">
                        {error.message}
                    </p>
                ) : null}

                <div ref={actionsRef} className="mt-auto flex flex-col gap-2 pt-6">
                    <Button type="submit" fullWidth busy={busy} disabled={!canSubmit}>
                        {isSignup ? '가입하기' : '로그인'}
                    </Button>
                    <Button variant="text" fullWidth onClick={switchMode}>
                        {isSignup ? '이미 단골이에요. 로그인할게요' : '처음 왔어요. 가입할게요'}
                    </Button>
                </div>
            </form>
        </main>
    );
}
